import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { buildApiUrl } from "../../lib/api";
import {
    allowsManualEarningsDate,
    fetchEarningsOpportunity,
    getEarningsOpportunityNotice,
} from "../../lib/earningsOpportunity";
import { buildSimulationPayload } from "../../lib/strategySimulation";
import {
    isSmallAccountMode,
    isStrategyAllowedForBuyingPower,
    SMALL_ACCOUNT_REJECTION_MESSAGE,
    SMALL_ACCOUNT_TOOLTIP,
} from "../../lib/smallAccountMode";
import "./SimulatePanel.css";

// ------------------------------------------------------------------ //
// Constants                                                            //
// ------------------------------------------------------------------ //

const STRATEGIES = [
    { id: "wheel",            label: "Wheel" },
    { id: "tactical_spreads", label: "Tactical Spreads", description: "Short-horizon bull call debit spreads · 30–60 DTE" },
    { id: "regime_spreads",   label: "Regime Spreads",   description: "Long-horizon bull call debit spreads · 180–360 DTE" },
    { id: "earnings",         label: "Earnings",          description: "Strategy sized around an upcoming earnings catalyst" },
];

const PRESETS = [
    { id: "small_account", label: "Small Account", description: "Under $5K · 5–10 DTE · δ 0.25–0.40", capital: 2500 },
    { id: "conservative",  label: "Conservative",  description: "Low risk · 21–45 DTE · δ 0.15–0.25", capital: 10000 },
    { id: "balanced",      label: "Balanced",      description: "Moderate · 7–30 DTE · δ 0.25–0.35", capital: 25000  },
    { id: "aggressive",    label: "Aggressive",    description: "High yield · 3–14 DTE · δ 0.35–0.50", capital: 50000 },
];

const DAYS_OPTIONS = [30, 60, 90, 180];

// ------------------------------------------------------------------ //
// Helpers                                                              //
// ------------------------------------------------------------------ //

function fmt(n, decimals = 0) {
    return Number(n).toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function pct(n, decimals = 1) {
    return `${n >= 0 ? "+" : ""}${(n * 100).toFixed(decimals)}%`;
}

function fmtDate(dateStr) {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-").map(Number);
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${MONTHS[m - 1]} ${d}`;
}

function daysLabel(n) {
    if (n === 0) return "Today";
    if (n === 1) return "Tomorrow";
    return `${n}d away`;
}

function InlineSpinner() {
    return <span className="sp-inline-spinner" aria-hidden="true" />;
}

function scorePct(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `${Math.round(Number(value) * 100)}%`;
}

export function EarningsNotFoundState({ earningsData, earningsSymbol, manualDate, onManualDateChange }) {
    const earningsStatusMessage = getEarningsOpportunityNotice(earningsData, earningsSymbol);
    const showManualDateInput = allowsManualEarningsDate(earningsData);

    return (
        <div className="sp-earnings-not-found">
            <div className="sp-earnings-empty">
                {earningsStatusMessage}
            </div>
            {showManualDateInput ? (
                <div className="sp-manual-date-section">
                    <label className="sp-manual-date-label">Enter expected earnings date</label>
                    <input
                        type="date"
                        className="sp-date-input"
                        value={manualDate || ""}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={(e) => {
                            if (e.target.value) onManualDateChange?.(e.target.value);
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
}

// ------------------------------------------------------------------ //
// Helpers                                                              //
// ------------------------------------------------------------------ //

function buildSyntheticRecord(symbol, dateStr) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const earningsDate = new Date(dateStr + "T00:00:00");
    const daysTo = Math.max(0, Math.round((earningsDate - today) / 86_400_000));
    return {
        found: true,
        symbol,
        synthetic: true,
        earnings: { earnings_date: dateStr, days_to_earnings: daysTo, iv_rank: null, price: 0 },
        opportunities: [],
        best_opportunity: null,
    };
}

function loadManualDates() {
    try { return JSON.parse(localStorage.getItem("earningsManualDates") || "{}"); }
    catch { return {}; }
}

function saveManualDate(symbol, dateStr) {
    const all = loadManualDates();
    all[symbol] = dateStr;
    localStorage.setItem("earningsManualDates", JSON.stringify(all));
}

// ------------------------------------------------------------------ //
// Earnings sub-components                                              //
// ------------------------------------------------------------------ //

function EarningsWindow({ data }) {
    const e = data.earnings;
    return (
        <div className="sp-earnings-window">
            <div className="sp-earnings-window-left">
                <span className="sp-earnings-kicker">
                    Next earnings
                    {data.synthetic && <span className="sp-earnings-user-badge">user-entered</span>}
                </span>
                <span className="sp-earnings-date-val">{fmtDate(e.earnings_date)}</span>
                <span className="sp-earnings-tag">{daysLabel(e.days_to_earnings)}</span>
            </div>
            <div className="sp-earnings-meta">
                {e.iv_rank != null && (
                    <span>IV Rank <strong>{Number(e.iv_rank).toFixed(0)}</strong></span>
                )}
                {e.price > 0 && (
                    <span>Price <strong>${Number(e.price).toFixed(2)}</strong></span>
                )}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ //
// Results display                                                      //
// ------------------------------------------------------------------ //

function OutcomeBar({ p10, p25, p50, p75, p90, initial }) {
    const low   = Math.min(p10, initial * 0.85);
    const high  = Math.max(p90, initial * 1.30);
    const range = high - low;
    if (range <= 0) return null;

    const norm    = (v) => ((v - low) / range) * 100;
    const midPct  = norm(p50);
    const p10Pct  = norm(p10);
    const p90Pct  = norm(p90);
    const initPct = norm(initial);

    return (
        <div className="sp-bar-wrap" title={`P10: $${fmt(p10)} · P50: $${fmt(p50)} · P90: $${fmt(p90)}`}>
            <div className="sp-bar-track">
                <div className="sp-bar-range" style={{ left: `${p10Pct}%`, width: `${p90Pct - p10Pct}%` }} />
                <div className="sp-bar-median" style={{ left: `${midPct}%` }} />
                <div className="sp-bar-init"   style={{ left: `${initPct}%` }} />
            </div>
            <div className="sp-bar-labels">
                <span className="sp-bar-label sp-bar-label--left">${fmt(p10)}</span>
                <span className="sp-bar-label sp-bar-label--mid">${fmt(p50)}</span>
                <span className="sp-bar-label sp-bar-label--right">${fmt(p90)}</span>
            </div>
        </div>
    );
}

function SimulationRealismSummary({ realism }) {
    if (!realism) return null;

    const warnings = Array.isArray(realism.missing_data_warnings)
        ? realism.missing_data_warnings.filter(Boolean).slice(0, 4)
        : [];
    const providerSummary = realism.provider_awareness || {};
    const providersUsed = Array.isArray(providerSummary.providers_used)
        ? providerSummary.providers_used
        : (Array.isArray(realism.provider_ids_used) ? realism.provider_ids_used : []);

    return (
        <div className="sp-realism">
            <div className="sp-realism-top">
                <div>
                    <div className="sp-section-label">Simulation realism</div>
                    <div className="sp-realism-title">{realism.realism_label || "Simulation realism"}</div>
                </div>
                <div className="sp-realism-chip-row">
                    <span className="sp-realism-chip">Realism {scorePct(realism.realism_score)}</span>
                    <span className="sp-realism-chip">Synthetic {scorePct(realism.synthetic_dependency_ratio)}</span>
                    <span className="sp-realism-chip">Provider {scorePct(realism.provider_completeness_score)}</span>
                </div>
            </div>

            {realism.deployment_implication && (
                <div className="sp-realism-copy">{realism.deployment_implication}</div>
            )}

            <div className="sp-realism-grid">
                <div className="sp-realism-metric">
                    <span className="sp-realism-metric-label">Historical coverage</span>
                    <span className="sp-realism-metric-value">{scorePct(realism.historical_data_coverage)}</span>
                </div>
                <div className="sp-realism-metric">
                    <span className="sp-realism-metric-label">Fill realism</span>
                    <span className="sp-realism-metric-value">{scorePct(realism.fill_realism_score)}</span>
                </div>
            </div>

            {providersUsed.length > 0 && (
                <div className="sp-realism-provider-note">
                    Providers: {providersUsed.join(", ")}
                </div>
            )}

            {warnings.length > 0 && (
                <ul className="sp-realism-warnings">
                    {warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SimResults({ result, initial }) {
    const fc        = result.final_capitals;
    const growthPct = result.probability_of_growth * 100;
    const ruinPct   = result.probability_of_ruin   * 100;
    const avgReturn = result.median_total_return_pct;
    const isStrategyOnly = result.simulation_type === "strategy";

    return (
        <motion.div
            className="sp-results"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
        >
            {isStrategyOnly && (
                <div className="sp-sim-type-notice">
                    No current trade available. This simulation reflects general strategy behavior, not a specific trade.
                </div>
            )}
            <div className="sp-prob-row">
                <div className="sp-prob-item">
                    <span className="sp-prob-label">Growth probability</span>
                    <span className="sp-prob-value" style={{ color: growthPct >= 60 ? "#4ade80" : growthPct >= 40 ? "#fbbf24" : "#f87171" }}>
                        {growthPct.toFixed(0)}%
                    </span>
                </div>
                <div className="sp-prob-divider" />
                <div className="sp-prob-item">
                    <span className="sp-prob-label">Risk of ruin</span>
                    <span className="sp-prob-value" style={{ color: ruinPct <= 5 ? "#4ade80" : ruinPct <= 15 ? "#fbbf24" : "#f87171" }}>
                        {ruinPct.toFixed(1)}%
                    </span>
                </div>
                <div className="sp-prob-divider" />
                <div className="sp-prob-item">
                    <span className="sp-prob-label">Median return</span>
                    <span className="sp-prob-value" style={{ color: avgReturn >= 0 ? "#4ade80" : "#f87171" }}>
                        {pct(avgReturn)}
                    </span>
                </div>
            </div>

            <div className="sp-section-label">Outcome distribution · {result.n_simulations} runs</div>
            <OutcomeBar p10={fc.p10} p25={fc.p25} p50={fc.p50} p75={fc.p75} p90={fc.p90} initial={initial} />

            <div className="sp-cases">
                <div className="sp-case">
                    <span className="sp-case-label">Worst case (P10)</span>
                    <span className="sp-case-value sp-case--worst">${fmt(fc.p10)}</span>
                    <span className="sp-case-delta" style={{ color: fc.p10 >= initial ? "#4ade80" : "#f87171" }}>
                        {pct((fc.p10 - initial) / initial)}
                    </span>
                </div>
                <div className="sp-case">
                    <span className="sp-case-label">Avg outcome (P50)</span>
                    <span className="sp-case-value sp-case--avg">${fmt(fc.p50)}</span>
                    <span className="sp-case-delta" style={{ color: fc.p50 >= initial ? "#4ade80" : "#f87171" }}>
                        {pct((fc.p50 - initial) / initial)}
                    </span>
                </div>
                <div className="sp-case">
                    <span className="sp-case-label">Best case (P90)</span>
                    <span className="sp-case-value sp-case--best">${fmt(fc.p90)}</span>
                    <span className="sp-case-delta" style={{ color: "#4ade80" }}>
                        {pct((fc.p90 - initial) / initial)}
                    </span>
                </div>
            </div>

            <SimulationRealismSummary realism={result.simulation_realism} />

            <div className="sp-footnote">
                Based on {result.n_simulations} Monte Carlo runs · {result.days}d horizon
            </div>
        </motion.div>
    );
}

// ------------------------------------------------------------------ //
// Main component                                                       //
// ------------------------------------------------------------------ //

const SimulatePanel = ({ defaultBuyingPower, symbol, launchRequest = null }) => {
    const [mode,    setMode]    = useState("wheel");
    const [preset,  setPreset]  = useState(isSmallAccountMode(defaultBuyingPower) ? "small_account" : "balanced");
    const [capital, setCapital] = useState(defaultBuyingPower ?? 2500);
    const [days,    setDays]    = useState(90);
    const [result,  setResult]  = useState(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);

    // Earnings mode state
    const [earningsSymbol,    setEarningsSymbol]    = useState((symbol || "AAPL").toUpperCase());
    const [symbolDraft,       setSymbolDraft]       = useState((symbol || "AAPL").toUpperCase());
    const [editingSymbol,     setEditingSymbol]     = useState(false);
    const [earningsData,      setEarningsData]      = useState(null);
    const [earningsLoading,   setEarningsLoading]   = useState(false);
    const [earningsError,     setEarningsError]     = useState(null);
    const [earningsDirection, setEarningsDirection] = useState(null);
    const [manualDate,        setManualDate]        = useState(null);
    const [showStratPicker,   setShowStratPicker]   = useState(false);
    const [pendingLaunch,     setPendingLaunch]     = useState(null);
    const symbolInputRef = useRef(null);

    const selectedOpp = earningsData?.opportunities?.find(
        (o) => o.strategy === earningsDirection
    ) ?? earningsData?.best_opportunity ?? null;
    const smallAccountMode = isSmallAccountMode(defaultBuyingPower);
    const selectedStrategyAllowed = isStrategyAllowedForBuyingPower(mode, defaultBuyingPower);

    // Sync symbol chip when Dashboard's active ticker changes
    useEffect(() => {
        if (symbol) {
            const s = symbol.toUpperCase();
            setEarningsSymbol(s);
            setSymbolDraft(s);
        }
    }, [symbol]);

    useEffect(() => {
        setCapital(defaultBuyingPower ?? 2500);
    }, [defaultBuyingPower]);

    // Fetch earnings whenever earnings mode is active or symbol changes
    const fetchEarnings = useCallback(async (sym) => {
        if (!sym) return;
        setEarningsLoading(true);
        setEarningsData(null);
        setEarningsError(null);
        setEarningsDirection(null);
        setManualDate(null);
        try {
            const data = await fetchEarningsOpportunity(sym, capital);
            if (data.found) {
                setEarningsData(data);
                if (data.best_opportunity) setEarningsDirection(data.best_opportunity.strategy);
            } else {
                // Check for a previously stored manual date for this symbol
                const stored = loadManualDates()[sym];
                if (stored && allowsManualEarningsDate(data)) {
                    setManualDate(stored);
                    setEarningsData(buildSyntheticRecord(sym, stored));
                } else {
                    setEarningsData(data); // found: false → show date picker
                }
            }
        } catch (err) {
            // 404 means the route couldn't find earnings — fall through to date picker
            if (err?.response?.status === 404) {
                const stored = loadManualDates()[sym];
                if (stored) {
                    setManualDate(stored);
                    setEarningsData(buildSyntheticRecord(sym, stored));
                } else {
                    setEarningsData({ found: false, symbol: sym });
                }
            } else {
                setEarningsError("Could not load earnings data.");
            }
        } finally {
            setEarningsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (mode === "earnings" && selectedStrategyAllowed) fetchEarnings(earningsSymbol);
    }, [mode, earningsSymbol, fetchEarnings, selectedStrategyAllowed]);

    // Symbol chip actions
    const applySymbol = () => {
        const s = symbolDraft.trim().toUpperCase();
        setEditingSymbol(false);
        if (s && s !== earningsSymbol) {
            setEarningsSymbol(s);
            setResult(null);
        } else {
            setSymbolDraft(earningsSymbol);
        }
    };

    const applyManualDate = (sym, dateStr) => {
        if (!dateStr) return;
        saveManualDate(sym, dateStr);
        setManualDate(dateStr);
        setEarningsData(buildSyntheticRecord(sym, dateStr));
    };

    const handleModeChange = (m) => {
        if (!isStrategyAllowedForBuyingPower(m, defaultBuyingPower)) {
            setError(SMALL_ACCOUNT_REJECTION_MESSAGE);
            setShowStratPicker(false);
            return;
        }
        setMode(m);
        setResult(null);
        setError(null);
        setShowStratPicker(false);
    };

    const run = async (override = null) => {
        const { effectiveMode, payload } = buildSimulationPayload({
            override,
            mode,
            capital,
            days,
            preset,
            selectedOpp,
        });
        console.log(`[sim] run: mode="${effectiveMode}" override_mode="${override?.mode ?? "none"}" state_mode="${mode}"`);
        if (!isStrategyAllowedForBuyingPower(effectiveMode, defaultBuyingPower)) {
            setError(SMALL_ACCOUNT_REJECTION_MESSAGE);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.post(
                buildApiUrl("/simulation/monte-carlo"),
                payload,
                { withCredentials: true }
            );
            if (data?.status === "no_trades_possible") {
                setError("Not enough capital to open any trades with this strategy. Try increasing your starting capital.");
            } else {
                setResult(data);
            }
        } catch (err) {
            const errMsg = err.response?.data?.error || "Simulation failed. Try again.";
            setError(errMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!launchRequest?.token) return;

        const nextMode = launchRequest.mode || "wheel";
        setMode(nextMode);
        setError(null);
        setShowStratPicker(false);

        if (launchRequest.initial_capital != null) {
            setCapital(Number(launchRequest.initial_capital));
        }
        if (launchRequest.buying_power != null && launchRequest.initial_capital == null) {
            setCapital(Number(launchRequest.buying_power));
        }
        if (launchRequest.days != null) {
            setDays(Number(launchRequest.days));
        }
        if (launchRequest.preset) {
            setPreset(launchRequest.preset);
        }
        if (launchRequest.symbol) {
            const nextSymbol = String(launchRequest.symbol).toUpperCase();
            setEarningsSymbol(nextSymbol);
            setSymbolDraft(nextSymbol);
        }
        if (launchRequest.earningsDirection) {
            setEarningsDirection(launchRequest.earningsDirection);
        }

        if (nextMode === "earnings") {
            setPendingLaunch(launchRequest);
        } else {
            setPendingLaunch(null);
            window.requestAnimationFrame(() => {
                run(launchRequest);
            });
        }
    }, [launchRequest]);

    useEffect(() => {
        if (!pendingLaunch || mode !== "earnings" || earningsLoading || !earningsData?.found) return;
        if (pendingLaunch.earningsDirection) {
            setEarningsDirection(pendingLaunch.earningsDirection);
        }
        const requestToRun = pendingLaunch;
        setPendingLaunch(null);
        window.requestAnimationFrame(() => {
            run(requestToRun);
        });
    }, [pendingLaunch, mode, earningsLoading, earningsData]);

    const selectedPreset   = PRESETS.find((p) => p.id === preset);
    const selectedStrategy = STRATEGIES.find((s) => s.id === mode);
    const strategyDisabledMessage = !selectedStrategyAllowed ? SMALL_ACCOUNT_REJECTION_MESSAGE : null;

    const earningsRunDisabled = !selectedStrategyAllowed || (mode === "earnings" && (
        earningsLoading || !earningsData?.found
    ));

    return (
        <div className="sp-controls">

            {/* ── Strategy pills ─────────────────────────────────────── */}
            <div className="sp-field">
                <label className="sp-label">Strategy</label>
                {smallAccountMode && <span className="sp-small-account-badge">Small Account Mode</span>}
                <div className="sp-strategy-row">
                    {STRATEGIES.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            title={!isStrategyAllowedForBuyingPower(s.id, defaultBuyingPower) ? SMALL_ACCOUNT_TOOLTIP : ""}
                            className={`sp-strategy-btn ${mode === s.id ? "sp-strategy-btn--active" : ""} ${!isStrategyAllowedForBuyingPower(s.id, defaultBuyingPower) ? "sp-strategy-btn--disabled" : ""}`}
                            onClick={() => handleModeChange(s.id)}
                            disabled={!isStrategyAllowedForBuyingPower(s.id, defaultBuyingPower)}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                {!selectedStrategyAllowed && (
                    <div className="sp-error">{strategyDisabledMessage}</div>
                )}
                {mode !== "wheel" && mode !== "earnings" && selectedStrategy?.description && (
                    <div className="sp-preset-desc">{selectedStrategy.description}</div>
                )}
            </div>

            {/* ── Wheel preset row ───────────────────────────────────── */}
            {mode === "wheel" && (
                <div className="sp-field">
                    <label className="sp-label">Preset</label>
                    <div className="sp-preset-row">
                        {PRESETS.map((p) => (
                            <button
                                key={p.id}
                                className={`sp-preset-btn ${preset === p.id ? "sp-preset-btn--active" : ""}`}
                                onClick={() => {
                                    setPreset(p.id);
                                    setCapital(p.capital);
                                }}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {selectedPreset && (
                        <div className="sp-preset-desc">{selectedPreset.description}</div>
                    )}
                </div>
            )}

            {/* ── Earnings mode ──────────────────────────────────────── */}
            {mode === "earnings" && (
                <>
                    {/* Symbol chip */}
                    <div className="sp-field">
                        <label className="sp-label">Symbol</label>
                        <div className="sp-symbol-row">
                            {editingSymbol ? (
                                <input
                                    ref={symbolInputRef}
                                    className="sp-symbol-input"
                                    value={symbolDraft}
                                    onChange={(e) => setSymbolDraft(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter")  applySymbol();
                                        if (e.key === "Escape") { setSymbolDraft(earningsSymbol); setEditingSymbol(false); }
                                    }}
                                    onBlur={applySymbol}
                                    maxLength={5}
                                    autoFocus
                                    spellCheck={false}
                                />
                            ) : (
                                <button
                                    className="sp-symbol-chip"
                                    onClick={() => { setEditingSymbol(true); }}
                                >
                                    {earningsSymbol}
                                    <svg className="sp-symbol-edit-icon" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M7 1.5L8.5 3L3.5 8H2V6.5L7 1.5Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Earnings window + opportunities */}
                    {earningsLoading && (
                        <div className="sp-earnings-loading">
                            <span className="sp-earnings-loading-dot" />
                            Fetching earnings for {earningsSymbol}…
                        </div>
                    )}

                    {!earningsLoading && earningsError && (
                        <div className="sp-error">{earningsError}</div>
                    )}

                    {!earningsLoading && earningsData && !earningsData.found && (
                        <EarningsNotFoundState
                            earningsData={earningsData}
                            earningsSymbol={earningsSymbol}
                            manualDate={manualDate}
                            onManualDateChange={(value) => applyManualDate(earningsSymbol, value)}
                        />
                    )}

                    {!earningsLoading && earningsData?.found && (
                        <div className="sp-earnings-panel">
                            <EarningsWindow data={earningsData} />

                            {earningsData.synthetic && (
                                <div className="sp-manual-date-row">
                                    <span className="sp-manual-date-label">Change date</span>
                                    <input
                                        type="date"
                                        className="sp-date-input"
                                        value={manualDate || ""}
                                        min={new Date().toISOString().split("T")[0]}
                                        onChange={(e) => {
                                            if (e.target.value) applyManualDate(earningsSymbol, e.target.value);
                                        }}
                                    />
                                </div>
                            )}

                            {earningsData.opportunities?.length > 0 && (
                                <>
                                    <div className="sp-strat-selected-row">
                                        <div className="sp-strat-selected">
                                            <span className="sp-strat-selected-dir">
                                                {selectedOpp?.strategy === "call_debit_spread" ? "↑ Bullish" : "↓ Bearish"}
                                            </span>
                                            <span className="sp-strat-selected-name">
                                                {selectedOpp?.strategy === "call_debit_spread" ? "Call debit spread" : "Put debit spread"}
                                            </span>
                                            {selectedOpp && (
                                                <span className={`sp-opp-conf sp-opp-conf--${selectedOpp.confidence}`}>
                                                    {selectedOpp.confidence}
                                                </span>
                                            )}
                                            {selectedOpp && (
                                                <span className="sp-strat-score">
                                                    {selectedOpp.score}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            className="sp-strat-pencil"
                                            onClick={() => setShowStratPicker(p => !p)}
                                            title="Change strategy"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M8.5 1.5L10.5 3.5L4 10H2V8L8.5 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                    <AnimatePresence>
                                        {showStratPicker && (
                                            <motion.div
                                                className="sp-strat-list"
                                                initial={{ opacity: 0, y: -4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -4 }}
                                                transition={{ duration: 0.14 }}
                                            >
                                                {earningsData.opportunities.map((opp) => {
                                                    const isCall = opp.strategy === "call_debit_spread";
                                                    return (
                                                        <button
                                                            key={opp.strategy}
                                                            className={`sp-strat-item ${earningsDirection === opp.strategy ? "sp-strat-item--active" : ""}`}
                                                            onClick={() => { setEarningsDirection(opp.strategy); setShowStratPicker(false); }}
                                                        >
                                                            <div className="sp-strat-item-left">
                                                                <span className="sp-strat-item-dir">{isCall ? "↑ Bullish" : "↓ Bearish"}</span>
                                                                <span className="sp-strat-item-name">{isCall ? "Call debit spread" : "Put debit spread"}</span>
                                                            </div>
                                                            <div className="sp-strat-item-right">
                                                                <span className={`sp-opp-conf sp-opp-conf--${opp.confidence}`}>{opp.confidence}</span>
                                                                <span className="sp-strat-score">{opp.score}</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </>
                            )}

                            {earningsData.synthetic && (
                                <div className="sp-synthetic-note">
                                    No spread data available · simulation uses tactical spreads parameters
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ── Capital + Horizon ──────────────────────────────────── */}
            <div className="sp-row">
                <div className="sp-field sp-field--half">
                    <label className="sp-label">Starting capital</label>
                    <div className="sp-input-wrap">
                        <span className="sp-input-prefix">$</span>
                        <input
                            className="sp-input"
                            type="number"
                            min="100"
                            step="100"
                            value={capital}
                            onChange={(e) => setCapital(e.target.value)}
                        />
                    </div>
                </div>
                <div className="sp-field sp-field--half">
                    <label className="sp-label">Horizon</label>
                    <div className="sp-days-row">
                        {DAYS_OPTIONS.map((d) => (
                            <button
                                key={d}
                                className={`sp-days-btn ${days === d ? "sp-days-btn--active" : ""}`}
                                onClick={() => setDays(d)}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <motion.button
                className="sp-run-btn"
                onClick={run}
                disabled={loading || earningsRunDisabled}
                whileHover={{ opacity: (loading || earningsRunDisabled) ? 0.4 : 0.88 }}
                whileTap={{ scale: (loading || earningsRunDisabled) ? 1 : 0.97 }}
                transition={{ duration: 0.12 }}
            >
                {loading ? (
                    <span className="sp-btn-content">
                        <InlineSpinner />
                        Running…
                    </span>
                ) : mode === "earnings" ? "Simulate Earnings Play" : "Run Simulation"}
            </motion.button>

            {error && <div className="sp-error">{error}</div>}

            <AnimatePresence>
                {result && !loading && (
                    <SimResults result={result} initial={Number(capital)} />
                )}
            </AnimatePresence>
        </div>
    );
};

export default SimulatePanel;
